import adapter from './adapter';
import ApiDocumentCard from './entities/ApiDocumentCard';

export const DOCUMENT_KIND_COLLECTION = 'collection';
export const DOCUMENT_KIND_CHECKLIST = 'checklist';
export const DOCUMENT_KIND_INDEX = 'index';

export const DOCUMENT_STATUS_ACTIVE = '1';
export const DOCUMENT_STATUS_DISABLED = '0';
export const DOCUMENT_STATUS_PARTLY = '2';
export const DOCUMENT_STATUS_PROJECT = '4';
export const DOCUMENT_STATUS_UNDEFINED = '8';

export const DOCUMENT_PROJECT_TYPE_AMENDMENT = 'amendment';
export const DOCUMENT_PROJECT_TYPE_REPLACEMENT = 'replacement';
export const DOCUMENT_PROJECT_STATUS_START = 'start';
export const DOCUMENT_PROJECT_STATUS_END = 'end';

export default Object.assign(Object.create(adapter), {
  /**
   * Возвращает объект поискового запроса, содержащий непустые параметры
   * исходного запроса searchQuery, приведенные к требуемому формату.
   *
   * @param {Object} searchQuery
   * @param {number} [defaultLimit]
   * @returns {Object}
   */
  prepareSearchQuery(searchQuery, defaultLimit = 20) {
    let params = { ...searchQuery };

    if (params.searchBy === 'designation') {
      params.title = params.text;
      delete params.text;
    }
    delete params.searchBy;
    Object.keys(params).forEach(key => {
      if (Array.isArray(params[key])) {
        params[key] = params[key].filter(val => val !== '').join(',');
      }

      if (!params[key] && params[key] !== 0) {
        delete params[key];
      }
    });

    params.limit = params.limit || defaultLimit;
    params.countTotal = params.countTotal ? 1 : 0;
    params.groupByDb = params.groupByDb ? 1 : 0;

    return params;
  },

  /**
   * Возвращает документы, соответствующие поисковому запросу query.
   * Параметр запроса groupByDb игнорируется.
   *
   * @param {Object} query
   * @param {CancelTokenSource} cancelSource
   * @returns {Promise<{
   *   items: Array<ApiDocumentSearchCardDef>,
   *   [total]: number
   * }>}
   */
  findDocuments(query, cancelSource) {
    let params = this.prepareSearchQuery(query);

    if (params.groupByDb) {
      delete params.groupByDb;
    }
    return this.getData('/documents', { params }, cancelSource);
  },

  /**
   * Возвращает список классификаторов.
   *
   * @returns {Promise<Array<ApiClassifierItem>>}
   */
  getClassifiers() {
    return this.getData('/documents.classifiers');
  },

  /**
   * Возвращает список классификаторов и/или папок классификаторов
   * с идентификаторами ids.
   *
   * @param {Array} ids
   * @returns {Promise<Array<ApiClassifierItem>>}
   */
  getClassifiersByIds(ids) {
    return this.getDataByIds('/documents.classifiers', ids);
  },

  /**
   * Возвращает папки классификатора с идентификатором classifierId.
   *
   * @param {string} classifierId
   * @returns {Promise<Array<ApiClassifierFolder>>}
   * @throws Error
   */
  getClassifierFolders(classifierId) {
    if (!classifierId) {
      throw new Error('Classifier identifier not defined.');
    }
    return this.getData(`/documents.classifiers/${classifierId}/folders`);
  },

  /**
   * Возвращает документы-перечни с идентификаторами ids.
   *
   * @param {Array} ids
   * @param {boolean} [groupByDb]
   * @returns {Promise<Array<ApiDocumentGroup>>|Promise<Array<ApiDocumentSearchCardDef>>}
   */
  getChecklistsByIds(ids, groupByDb = false) {
    return this.getData('/documents', {
      params: {
        ids: ids.join(','),
        groupByDb: groupByDb ? 1 : 0
      }
    }).then(data => (groupByDb ? data.groups : data.items));
  },

  /**
   * Возвращает документы-перечни и документы-указатели, наименование которых
   * содержит строку title, сгруппированные по базам документов.
   *
   * @param {string} title
   * @returns {Promise<Array<ApiDocumentGroup>>}
   */
  getChecklistsByTitle(title) {
    return this.getData('/documents', {
      params: {
        title: title,
        kinds: `${DOCUMENT_KIND_CHECKLIST},${DOCUMENT_KIND_INDEX}`,
        groupByDb: 1
      }
    }).then(data => data.groups);
  },

  /**
   * Возвращает документы-перечни и документы-указатели, в которые входят документы,
   * соответствующие поисковому запросу query, сгруппированные по базам документов.
   *
   * @param {Object} query
   * @param {number} limit
   * @returns {Promise<Array<ApiDocumentGroup>>}
   */
  getChecklistsByDocuments(query, limit = 20) {
    let params = this.prepareSearchQuery(query);
    params.limit = limit;
    params.offset = 0;
    params.countTotal = 0;
    params.groupByDb = 1;

    return this.getData('/documents.checklists', { params }).then(
      data => data.groups
    );
  },

  getStatuses() {
    return this.getData('/documents.statuses');
  },

  /**
   * Возвращает список типов (индексов) документов.
   *
   * @param {?string} [prefix]
   * @returns {Promise<Array<Object>>}
   */
  getTypes(prefix = null) {
    let params = prefix ? { prefix } : null;

    return this.getData('/documents.types', { params });
  },

  /**
   * Возвращает список типов (индексов) документов с идентификаторами ids.
   *
   * @param {Array} ids
   * @returns {Promise<Array<Object>>}
   */
  getTypesByIds(ids) {
    return this.getDataByIds('/documents.types', ids);
  },

  /**
   * Возвращает список организаций.
   * Если передан параметр name, то метод возвращает только организации,
   * название которых включает соответствующую строку.
   *
   * @param {?string} [name]
   * @returns {Promise<Array<Object>>}
   */
  getOrganizations(name = null) {
    let params = { limit: 100 };

    if (name) {
      params.name = name;
    }
    return this.getData('/documents.organizations', { params });
  },

  /**
   * Возвращает список организаций с идентификаторами ids.
   *
   * @param {Array} ids
   * @returns {Promise<Array<Object>>}
   */
  getOrganizationsByIds(ids) {
    return this.getDataByIds('/documents.organizations', ids);
  },

  /**
   * Возвращает список организаций, разработавших документ.
   * Если передан параметр name, то метод возвращает только организации,
   * название которых включает соответствующую строку.
   *
   * @param {?string} [name]
   * @returns {Promise<Array<Object>>}
   */
   getDesigners(name = null) {
    let params = { limit: 100 };

    if (name) {
      params.name = name;
    }
    return this.getData('/documents.designers', { params });
  },

  /**
   * Возвращает список организаций, разработавших документ, с идентификаторами ids.
   *
   * @param {Array} ids
   * @returns {Promise<Array<Object>>}
   */
  getDesignersByIds(ids) {
    return this.getDataByIds('/documents.designers', ids);
  },

  /**
   * Возвращает массив форматов документа.
   *
   * @param {string} documentId
   * @returns {Promise<Array<string>>}
   */
  getFormat(documentId) {
    return this.getData(`/documents/${documentId}/format`);
  },

  /**
   * Возвращает информацию о документе.
   *
   * @param {string} documentId
   * @param {?CancelTokenSource} [cancelSource]
   * @param {boolean|Array<string>} [relations=true]
   * @returns {Promise<ApiDocumentCard>}
   */
  getInfo(documentId, cancelSource = null, relations = true) {
    let params = {
      relations: Array.isArray(relations)
        ? relations.join(',')
        : relations
        ? 'on'
        : 'off'
    };

    return this.getData(
      `/documents/${documentId}/info`,
      { params },
      cancelSource
    ).then(data => new ApiDocumentCard(data));
  },

  /**
   * Возвращает массив классификаторов, в которые входит документ,
   * сгруппированных по базам документов.
   *
   * @param {string} documentId
   * @param {?CancelTokenSource} [cancelSource]
   * @returns {Promise<Array<ApiDocumentClassifiers>>}
   */
  getDocumentClassifiers(documentId, cancelSource = null) {
    return this.getData(
      `/documents/${documentId}/classifiers`,
      null,
      cancelSource
    );
  },

  /**
   * Возвращает массив изменений документа.
   *
   * @param {string} documentId
   * @param {?CancelTokenSource} [cancelSource]
   * @returns {Promise<Array<ApiDocumentChange>>}
   */
  getChanges(documentId, cancelSource = null) {
    return this.getData(`/documents/${documentId}/changes`, null, cancelSource);
  },

  /**
   * Возвращает HTML-код описания преемственности документа.
   *
   * @param {string} documentId
   * @param {?CancelTokenSource} [cancelSource]
   * @returns {ApiDocumentContinuity}
   */
  getContinuity(documentId, cancelSource = null) {
    return this.getData(
      `/documents/${documentId}/continuity`,
      null,
      cancelSource
    );
  },

  /**
     * Возвращает HTML-код "Примечания и особенности применения".
     *
     * @param {string} documentId
     * @param {?CancelTokenSource} [cancelSource]
     * @returns {ApiDocumentAnnotation}
     */
  getAnnotation(documentId, cancelSource = null) {
    return this.getData(
      `/documents/${documentId}/annotation`,
      null,
      cancelSource
    );
  },

  /**
   * Возвращает историю документа.
   *
   * @param {string} documentId
   * @param {?CancelTokenSource} [cancelSource]
   * @returns {Array<ApiDocumentHistoryItem>}
   */
  getHistory(documentId, cancelSource = null) {
    return this.getData(`/documents/${documentId}/history`, null, cancelSource);
  },

  /**
   * Возвращает комментарии обсуждений документа.
   *
   * @param {string} documentId
   * @param {?CancelTokenSource} [cancelSource]
   * @returns {Array<Array<ApiDocumentDiscussionComment>>}
   */
  getDiscussions(documentId, cancelSource = null) {
    return this.getData(
      `/documents/${documentId}/discussions`,
      null,
      cancelSource
    );
  },
  
  /**
   * Возвращает редакции документа.
   *
   * @param {string} documentId
   * @param {?CancelTokenSource} [cancelSource]
   * @returns {Array<ApiDocumentRevisionItem>}
   */
  getRevisions(documentId, cancelSource = null) {
    return this.getData(`/documents/${documentId}/revisions`, null, cancelSource);
  },
  
  /**
   * Возвращает приложения документа.
   *
   * @param {string} documentId
   * @param {?CancelTokenSource} [cancelSource]
   * @returns {Array<ApiDocumentAttachmentItem>}
   */
   getAttachments(documentId, cancelSource = null) {
    return this.getData(`/documents/${documentId}/attachments`, null, cancelSource);
  },

  /**
   * Возвращает содержимое HTML-тега <head> документа,
   * а также информацию о формате, структуре и HTML-атрибутах документа.
   *
   * @param {string} documentId
   * @returns {Promise<{
   *   id: string,
   *   format: string,
   *   docType: string,
   *   htmlAttrs: string,
   *   headContent: string,
   *   bodyAttrs: string,
   *   blockCount: number
   * }>}
   */
  getHead(documentId) {
    return this.getData(`documents/${documentId}/head`);
  },

  /**
   * Возвращает блок текста документа по индексу блока.
   *
   * @param {string} documentId
   * @param {string|number} blockIndex
   * @param {CancelTokenSource} [cancelSource]
   * @returns {Promise<{
   *   index: number,
   *   content: string
   * }>}
   */
  getText(documentId, blockIndex, cancelSource = null) {
    return this.getData(
      `documents/${documentId}/text`,
      { params: { blockIndex } },
      cancelSource
    );
  },

  /**
   * Возвращает блок текста документа по идентификатору элемента-якоря.
   *
   * @param {string} documentId
   * @param {string} anchorId
   * @param {CancelTokenSource} [cancelSource]
   * @returns {Promise<{
   *   index: number,
   *   content: string
   * }>}
   */
  getTextByAnchorId(documentId, anchorId, cancelSource = null) {
    return this.getData(
      `documents/${documentId}/text`,
      { params: { anchorId } },
      cancelSource
    );
  },

  /**
   * Возвращает оглавление документа.
   *
   * @param {string} documentId
   * @param {?CancelTokenSource} [cancelSource]
   * @returns {Promise<Array<ApiDocumentTextContentsItem>>}
   */
  getTextContents(documentId, cancelSource = null) {
    return this.getData(
      `documents/${documentId}/text.contents`,
      null,
      cancelSource
    );
  },

  /**
   * Возвращает данные сканов страниц документа.
   *
   * @param {string} documentId
   * @param {CancelTokenSource} [cancelSource]
   * @returns {Promise<Array<ApiDocumentScanPage>>}
   */
  getScan(documentId, cancelSource = null) {
    return this.getData(`documents/${documentId}/scan`, null, cancelSource);
  },

  /**
   * Возвращает оглавление отсканированного документа.
   *
   * @param {string} documentId
   * @param {?CancelTokenSource} [cancelSource]
   * @returns {Promise<Array<ApiDocumentScanContentsItem>>}
   */
  getScanContents(documentId, cancelSource = null) {
    return this.getData(
      `documents/${documentId}/scan.contents`,
      null,
      cancelSource
    );
  },

  /**
   * Возвращает найденные совпадения с поисковой строкой в тексте
   * отсканированного документа.
   *
   * @param {string} documentId
   * @param {string} text
   * @param {?CancelTokenSource} cancelSource
   * @returns {Promise<Object<Array<Array>>>}
   */
  findByScan(documentId, text, cancelSource = null) {
    return this.getData(
      `/documents/${documentId}/scan.search`,
      { params: { text } },
      cancelSource
    );
  }
});
